"use client"
import { ArrowLeft, Eye, Users, TrendingUp, Target, Sparkles, Lightbulb, ArrowRight } from "lucide-react"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"
import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from "@/lib/supabase"
import { useAuth } from "@/contexts/AuthContext"

const mockCampaignData = {
  title: "New Album Promo",
  type: "Email Campaign",
  sentDate: "2024-05-15",
  summary: {
    deliveryRate: { value: "98.1%", count: "1,817 delivered", color: "blue" },
    openRate: { value: "42%", count: "761 opens", color: "green" },
    clickRate: { value: "15%", count: "272 clicks", color: "purple" },
    conversionRate: { value: "7%", count: "127 conversions", color: "orange" },
  },
  chartData: [
    { day: "Day 1", opens: 160, clicks: 45 },
    { day: "Day 2", opens: 140, clicks: 38 },
    { day: "Day 3", opens: 120, clicks: 32 },
    { day: "Day 4", opens: 100, clicks: 34 },
    { day: "Day 5", opens: 85, clicks: 28 },
    { day: "Day 6", opens: 70, clicks: 25 },
    { day: "Day 7", opens: 60, clicks: 22 },
  ],
  topLinks: [
    { name: "Stream the New Album", clicks: 320, percentage: 45 },
    { name: "Merch Store", clicks: 200, percentage: 35 },
    { name: "Tour Dates", clicks: 142, percentage: 20 },
  ],
  insights: {
    performance: "This campaign had the highest click rate among casual fans in the past 6 months (15% vs 9% average).",
    optimization:
      "Album streaming links performed 3x better than merch. Consider leading with music content in future campaigns.",
    nextSteps: "Send a follow-up campaign to the 634 fans who opened but didn't click within 48 hours.",
  },
  messageContent: {
    subject: "🎵 New Album Drop - Stream Now!",
    preview: "Get exclusive early access to our latest tracks...",
    content:
      "Hey music lover! We're thrilled to announce our brand new album is now live on all streaming platforms. As one of our most dedicated fans, you get first access to these incredible new tracks that we've been working on for months.",
  },
  stats: {
    recipients: "1,847",
    bounceRate: "1.9%",
    unsubscribeRate: "0.3%",
    avgTimeSpent: "2m 34s",
  },
}

const CampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        // campaigns are stored in profiles.campaigns as JSON strings (per CreateCampaign)
        const { data, error } = await supabase
          .from('profiles')
          .select('campaigns')
          .eq('id', user?.id)
          .single();
        if (error) {
          // fall back to mock data
          setCampaign(null);
          return;
        }
        const arr = (data?.campaigns || []).map((c) => {
          try { return JSON.parse(c); } catch { return null; }
        }).filter(Boolean);
        const matchById = arr.find((c) => String(c?.id) === String(id));
        const matchByIndex = arr[Number(id)];
        setCampaign(matchById || matchByIndex || null);
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) load();
  }, [id, user?.id]);

  const data = useMemo(() => {
    // helper: convert seconds -> human readable
    const formatDuration = (sec) => {
      const s = Math.floor(Number(sec) || 0)
      const m = Math.floor(s / 60)
      const r = s % 60
      return m > 0 ? `${m}m ${r}s` : `${r}s`
    }

    const recipients = Number(campaign?.recipientsCount || campaign?.messages_count) || 0
    const opens = Number(campaign?.opens) || 0
    const clicks = Number(campaign?.clicks) || 0
    const newFans = Number(campaign?.newFans) || 0
    const deliveryRate = typeof campaign?.deliveryRate === 'number' ? campaign.deliveryRate : 100
    const openRate = typeof campaign?.openRate === 'number' ? campaign.openRate : (recipients > 0 ? (opens / recipients) * 100 : 0)
    const clickRate = recipients > 0 ? (clicks / recipients) * 100 : 0
  // Always derive conversion rate from this campaign's data: new fans out of total clicks
  const conversionRate = clicks > 0 ? (newFans / clicks) * 100 : 0
    const bounceRate = typeof campaign?.bounceRate === 'number' ? campaign.bounceRate : 0
    const avgTimeSpent = Number(campaign?.avgTimeSpent) || 0

    const sentDate = campaign?.createdAt ? new Date(campaign.createdAt).toISOString().slice(0, 10) : mockCampaignData.sentDate

    const summary = {
      deliveryRate: { value: `${deliveryRate.toFixed(1)}%`, count: `${recipients} delivered`, color: 'blue' },
      openRate: { value: `${openRate.toFixed(1)}%`, count: `${opens} opens`, color: 'green' },
      clickRate: { value: `${clickRate.toFixed(1)}%`, count: `${clicks} clicks`, color: 'purple' },
      // Show conversions out of total clicks to reflect "new fans out of clicks" with friendly zero/pluralization
      conversionRate: {
        value: `${conversionRate.toFixed(1)}%`,
        count: clicks === 0 ? 'No clicks yet' : `${newFans} of ${clicks} ${clicks === 1 ? 'click' : 'clicks'}`,
        color: 'orange'
      },
    }

    const stats = {
      recipients: String(recipients),
      bounceRate: `${bounceRate.toFixed(1)}%`,
      avgTimeSpent: formatDuration(avgTimeSpent),
    }

    // Build chart data strictly from real stored timeSeries (no mock fallback)
    const buildLastNDates = (n) => {
      const arr = []
      const today = new Date()
      for (let i = n - 1; i >= 0; i--) {
        const d = new Date(today)
        d.setDate(today.getDate() - i)
        arr.push(d.toISOString().slice(0, 10))
      }
      return arr
    }
    const ts = (campaign?.timeSeries && typeof campaign.timeSeries === 'object') ? campaign.timeSeries : {}
    const last7 = buildLastNDates(7)
    const chartData = last7.map(date => {
      const vals = ts[date] || {}
      return { day: date, opens: Number(vals.opens) || 0, clicks: Number(vals.clicks) || 0 }
    })

    const merged = {
      ...mockCampaignData,
      ...campaign,
      sentDate,
      summary,
      chartData,
      topLinks: campaign?.topLinks ?? mockCampaignData.topLinks,
      insights: { ...mockCampaignData.insights, ...(campaign?.insights || {}) },
      messageContent: {
        ...mockCampaignData.messageContent,
        subject: campaign?.subject || mockCampaignData.messageContent.subject,
        content: campaign?.message || mockCampaignData.messageContent.content,
      },
      stats,
    }
    if (campaign?.subject) merged.title = campaign.subject
    if (campaign?.type) merged.type = `${String(campaign.type).toUpperCase()} Campaign`
    return merged
  }, [campaign]);

  const handleBack = () => {
    navigate('/campaigns');
  }

  const handleExportReport = () => {
    // Mock export functionality
  }

  const handleDuplicateCampaign = () => {
    // Mock duplicate functionality
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{data.title}</h1>
              <p className="text-gray-600 dark:text-gray-400">
                {data.type} • Sent on {data.sentDate}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportReport}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20
 transition-colors flex items-center gap-2">
              Export Report
            </button>
          </div>
        </div>

        {/* Campaign Summary */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Campaign Summary</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(data.summary).map(([key, data]) => (
              <div
                key={key}
                className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div
                    className={`w-12 h-12 bg-${data.color}-50 dark:bg-${data.color}-500/10 rounded-lg flex items-center justify-center`}
                  >
                    {key === "deliveryRate" && (
                      <Eye className={`w-6 h-6 text-${data.color}-600 dark:text-${data.color}-400`} />
                    )}
                    {key === "openRate" && (
                      <TrendingUp className={`w-6 h-6 text-${data.color}-600 dark:text-${data.color}-400`} />
                    )}
                    {key === "clickRate" && (
                      <Users className={`w-6 h-6 text-${data.color}-600 dark:text-${data.color}-400`} />
                    )}
                    {key === "conversionRate" && (
                      <Target className={`w-6 h-6 text-${data.color}-600 dark:text-${data.color}-400`} />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{data.value}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {key === "deliveryRate" && "Delivery Rate"}
                    {key === "openRate" && "Open Rate"}
                    {key === "clickRate" && "Click Rate"}
                    {key === "conversionRate" && "Conversion Rate"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{data.count}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Opens & Clicks Over Time */}
            <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Opens & Clicks Over Time</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="opens"
                      stroke="#06b6d4"
                      strokeWidth={2}
                      dot={{ fill: "#06b6d4", strokeWidth: 2, r: 4 }}
                      name="Opens"
                    />
                    <Line
                      type="monotone"
                      dataKey="clicks"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                      name="Clicks"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-4">
                Campaign engagement tracked over 7 days
              </p>
            </div>

            {/* Campaign Details */}
            <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Campaign Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4">Message Content</h4>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject Line</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg">
                        {data.messageContent.subject}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preview Text</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg">
                        {data.messageContent.preview}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4">Campaign Stats</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Recipients</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {data.stats.recipients}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Avg. Time Spent</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {data.stats.avgTimeSpent}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
export default CampaignDetails;
